<template>
  <div class="container p-4">
    <h2 class="text-center mb-3 font-weight-bold"> مدیریت Dataset </h2>
    <button @click="goToDashboard" class="btn btn-primry mb-3">
      بازگشت به میزکار
    </button>
    <!-- وضعیت لودینگ -->
    <div v-if="loading" class="text-center my-5">
      <div
        class="spinner-border text-primary"
        style="width: 3rem; height: 3rem"
        role="status"
      >
        <span class="sr-only">در حال بارگذاری...</span>
      </div>
      <p class="mt-3 text-muted">در حال دریافت داده‌ها...</p>
    </div>

    <!-- پیغام‌های موفقیت/خطا -->
    <div
      v-if="message.text"
      :class="`alert alert-${message.type} alert-dismissible fade show shadow-sm`"
      role="alert"
    >
      {{ message.text }}
      <button
        type="button"
        class="close"
        @click="clearMessage"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- جستجو -->
    <div class="row mb-4">
      <div class="col-md-6 mb-3 mb-md-0">
        <div class="input-group input-group-lg">
          <input
            v-model="question"
            @input="debouncedSearch"
            placeholder="جستجو سوال..."
            class="form-control"
          />
          <div class="input-group-prepend">
            <span class="input-group-text bg-light">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="input-group input-group-lg">
          <input
            v-model="answer"
            @input="debouncedSearch"
            placeholder="جستجو جواب..."
            class="form-control"
          />
          <div class="input-group-prepend">
            <span class="input-group-text bg-light">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- فرم -->
    <div class="card mb-4 shadow-sm">
      <div class="card-body">
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label class="font-weight-bold">سوال</label>
            <textarea
              v-model="form.question"
              placeholder="سوال"
              class="form-control"
              rows="2"
              required
            ></textarea>
          </div>
          <div class="form-group">
            <label class="font-weight-bold">جواب</label>
            <textarea
              v-model="form.answer"
              placeholder="جواب"
              class="form-control"
              rows="8"
              required
            ></textarea>
          </div>
          <div class="d-flex justify-content-end">
            <button
              type="submit"
              class="btn btn-primary btn-lg mr-2"
              :disabled="formProcessing"
            >
              <span
                v-if="formProcessing"
                class="spinner-border spinner-border-sm mr-2"
              ></span>
              {{ form._id ? "ویرایش" : "افزودن" }}
            </button>
            <button
              v-if="form._id"
              @click.prevent="resetForm"
              class="btn btn-outline-secondary btn-lg"
              :disabled="formProcessing"
            >
              لغو
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- جدول -->
    <div class="table-responsive mb-4">
      <table class="table table-striped table-bordered table-hover">
        <thead class="thead-dark">
          <tr>
            <th width="4%">آیدی</th>
            <th width="32%">سوال</th>
            <th width="32%">جواب</th>
            <th width="20%">زمان ایجاد</th>
            <th width="10%" class="text-center">عملیات</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item._id">
            <td class="text-center">
              {{ item._id }}
            </td>
            <td class="text-center">
              <div v-if="editingId === item._id">
                <textarea
                  v-model="editForm.question"
                  class="form-control"
                  rows="3"
                  @blur="saveEdit(item._id)"
                ></textarea>
              </div>
              <div v-else @click="startEditing(item)" class="editable-cell">
                {{ item.question }}
              </div>
            </td>
            <td>
              <div v-if="editingId === item._id">
                <textarea
                  v-model="editForm.answer"
                  class="form-control"
                  rows="12"
                  @blur="saveEdit(item._id)"
                ></textarea>
              </div>
              <div v-else @click="startEditing(item)" class="editable-cell">
                {{ item.answer }}
              </div>
            </td>
            <td class="text-center">{{ formatDate(item.create_datetime) }}</td>
            <td class="text-center align-middle">
              <div
                class="d-flex justify-content-center align-items-center gap-1"
              >
                <!-- حالت ویرایش -->
                <template v-if="editingId === item._id">
                  <button
                    @click="saveEdit(item._id)"
                    class="btn btn-sm btn-outline-success"
                    title="ذخیره"
                  >
                    ✓
                  </button>
                  <button
                    @click="cancelEdit"
                    class="btn btn-sm mr-1 btn-outline-secondary"
                    title="لغو ویرایش"
                  >
                    ✖
                  </button>
                </template>

                <!-- حالت معمولی -->
                <template v-else>
                  <button
                    @click="startEditing(item)"
                    class="btn btn-sm btn-outline-primary"
                    title="ویرایش"
                  >
                    ✎
                  </button>
                </template>

                <!-- دکمه حذف در هر دو حالت -->
                <button
                  @click="deleteItem(item._id)"
                  class="btn btn-sm btn-outline-danger mr-1"
                  title="حذف"
                >
                  🗑
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && (!items || items.length === 0)">
            <td colspan="5" class="text-center text-muted py-5">
              <i class="bi bi-exclamation-circle text-danger"></i>
              <p class="mt-2">داده‌ای یافت نشد</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- صفحه‌بندی -->
    <div class="d-flex justify-content-between align-items-center flex-wrap">
      <div>
        <select
          v-model="limit"
          @change="changeLimit"
          class="custom-select custom-select-md"
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>

      <nav aria-label="Page navigation">
        <ul class="pagination mb-0">
          <li class="page-item" :class="{ disabled: page <= 1 }">
            <button
              class="page-link"
              @click="changePage(page - 1)"
              :disabled="page <= 1"
            >
              قبلی
            </button>
          </li>

          <template v-if="totalPages <= 7">
            <li
              v-for="n in totalPages"
              :key="n"
              class="page-item"
              :class="{ active: page === n }"
            >
              <button class="page-link" @click="changePage(n)">
                {{ n }}
              </button>
            </li>
          </template>
          <template v-else>
            <li class="page-item" :class="{ active: page === 1 }">
              <button class="page-link" @click="changePage(1)">1</button>
            </li>
            <li v-if="page > 3" class="page-item disabled">
              <span class="page-link">...</span>
            </li>
            <template v-if="page <= 3">
              <li
                v-for="n in [2, 3, 4]"
                :key="n"
                class="page-item"
                :class="{ active: page === n }"
              >
                <button class="page-link" @click="changePage(n)">
                  {{ n }}
                </button>
              </li>
            </template>
            <template v-else-if="page >= totalPages - 2">
              <li
                v-for="n in [totalPages - 3, totalPages - 2, totalPages - 1]"
                :key="n"
                class="page-item"
                :class="{ active: page === n }"
              >
                <button class="page-link" @click="changePage(n)">
                  {{ n }}
                </button>
              </li>
            </template>
            <template v-else>
              <li
                v-for="n in [page - 1, page, page + 1]"
                :key="n"
                class="page-item"
                :class="{ active: page === n }"
              >
                <button class="page-link" @click="changePage(n)">
                  {{ n }}
                </button>
              </li>
            </template>
            <li v-if="page < totalPages - 3" class="page-item disabled">
              <span class="page-link">...</span>
            </li>
            <li class="page-item" :class="{ active: page === totalPages }">
              <button class="page-link" @click="changePage(totalPages)">
                {{ totalPages }}
              </button>
            </li>
          </template>

          <li class="page-item" :class="{ disabled: page >= totalPages }">
            <button
              class="page-link"
              @click="changePage(page + 1)"
              :disabled="page >= totalPages"
            >
              بعدی
            </button>
          </li>
        </ul>
      </nav>
      <div class="border p-2">
       تعداد کل ایتم ها :  {{ totalItem }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "axios";
import { debounce } from "lodash";

const route = useRoute();
const widgetId = ref(route.params.widgetId);

// Refs
const items = ref([]);
const page = ref(1);
const totalPages = ref(1);
const limit = ref(25);
const question = ref("");
const answer = ref("");
const loading = ref(false);
const formProcessing = ref(false);
const message = ref({ text: "", type: "" });
const editingId = ref(null);
const editForm = ref({ question: "", answer: "" });
const totalItem=ref();

const form = ref({
  question: "",
  answer: "",
  widgetId: widgetId.value,
  _id: null,
});

const showMessage = (text, type = "success") => {
  message.value = { text, type };
  setTimeout(clearMessage, 5000);
};

const clearMessage = () => {
  message.value = { text: "", type: "" };
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const fetchItems = async () => {
  try {
    loading.value = true;
    const res = await axios.get(`${process.env.VUE_APP_API_GOOMAI}/datasets`, {
      params: {
        widgetId: widgetId.value,
        question: question.value,
        answer: answer.value,
        page: page.value,
        limit: limit.value,
      },
    });

    items.value = res.data.items;
    totalItem.value=res.data.total;
    totalPages.value = Math.ceil(res.data.total / limit.value);
  } catch (error) {
    console.error("Error fetching items:", error);
    showMessage("خطا در دریافت داده‌ها", "danger");
    items.value = [];
    totalPages.value = 1;
  } finally {
    loading.value = false;
  }
};

const debouncedSearch = debounce(() => {
  page.value = 1;
  fetchItems();
}, 500);

const changePage = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    page.value = newPage;
    fetchItems();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const changeLimit = () => {
  page.value = 1;
  fetchItems();
};

const submitForm = async () => {
  if (!form.value.question.trim() || !form.value.answer.trim()) {
    showMessage("لطفاً سوال و جواب را وارد کنید", "warning");
    return;
  }

  try {
    formProcessing.value = true;
    const action = form.value._id ? "ویرایش" : "افزودن";

    if (form.value._id) {
      await axios.put(
        `${process.env.VUE_APP_API_GOOMAI}/datasets/${form.value._id}`,
        form.value
      );
    } else {
      await axios.post(`${process.env.VUE_APP_API_GOOMAI}/datasets`, form.value);
    }

    resetForm();
    fetchItems();
    showMessage(`آیتم با موفقیت ${action} شد`);
  } catch (error) {
    console.error("Error submitting form:", error);
    showMessage(
      `خطا در ${form.value._id ? "ویرایش" : "افزودن"} داده`,
      "danger"
    );
  } finally {
    formProcessing.value = false;
  }
};

const startEditing = (item) => {
  editingId.value = item._id;
  editForm.value = { question: item.question, answer: item.answer };
};

const saveEdit = async (id) => {
  if (!editForm.value.question.trim() || !editForm.value.answer.trim()) {
    showMessage("لطفاً سوال و جواب را وارد کنید", "warning");
    return;
  }

  try {
    formProcessing.value = true;
    await axios.put(`${process.env.VUE_APP_API_GOOMAI}/datasets/${id}`, {
      question: editForm.value.question,
      answer: editForm.value.answer,
      widgetId: widgetId.value,
    });
    editingId.value = null;
    fetchItems();
    showMessage("آیتم با موفقیت ویرایش شد");
  } catch (error) {
    console.error("Error editing item:", error);
    showMessage("خطا در ویرایش داده", "danger");
  } finally {
    formProcessing.value = false;
  }
};

const deleteItem = async (id) => {
  if (!confirm("آیا از حذف این آیتم مطمئن هستید؟")) return;

  try {
    loading.value = true;
    await axios.delete(`${process.env.VUE_APP_API_GOOMAI}/datasets/${id}`);
    if (items.value.length === 1 && page.value > 1) {
      page.value -= 1;
    }
    fetchItems();
    showMessage("آیتم با موفقیت حذف شد");
  } catch (error) {
    console.error("Error deleting item:", error);
    showMessage("خطا در حذف داده", "danger");
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  form.value = {
    question: "",
    answer: "",
    widgetId: widgetId.value,
    _id: null,
  };
  editingId.value = null;
};
const cancelEdit = () => {
  editingId.value = null;
  editForm.value = { question: "", answer: "" };
};
const router = useRouter();

function goToDashboard() {
  router.push({
    name: "dashboard",
  });
}
onMounted(() => {
  fetchItems();
});

watch([page, limit], fetchItems);
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  direction: rtl;
}

.card {
  border: none;
  border-radius: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1) !important;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.322) !important;
}

.alert {
  border-radius: 8px;
  margin-bottom: 25px;
}

.form-control,
.custom-select {
  border-radius: 0;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.table {
  border-radius: 8px;
  overflow: hidden;
}

.table th {
  background-color: #343a40;
  color: white;
  font-weight: 600;
  padding: 15px;
}

.table td {
  vertical-align: middle;
  text-align: justify;
  padding: 15px;
  transition: background-color 0.2s ease;
}

.editable-cell {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.editable-cell:hover {
  background-color: #f8f9fa;
}

.btn {
  transition: all 0.3s ease;
}

.btn-outline-primary,
.btn-outline-danger {
  border-width: 2px;
}

.btn-outline-primary:hover {
  background-color: #007bff;
  color: white;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

.pagination .page-link {
  border-radius: 5px;
  margin: 0 3px;
  transition: all 0.3s ease;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
}

@media (max-width: 768px) {
  .table-responsive {
    border: none;
  }

  .table {
    font-size: 14px;
  }

  .btn {
    padding: 6px 12px;
  }
}
</style>
